import { Button, Modal, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { HtmlReactParser, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { Check24Icon, Clear24Icon } from "@kaltura/ds-react-icons";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";
import { Breakpoint } from "@mui/material";

export interface ApproveRejectProps {
    action: "approve" | "reject";
    requestConfirmation: boolean;
    onClick: () => void;
    loading: boolean;
    confirmationMessage: string;
    forceLargeButton?: boolean;
    noTextBreakpoint?: Breakpoint;
    isBulkAsync?: boolean;
    disabled?: boolean;
    report: (what: "act" | "act_confirm") => void;
}

const StyledButton = styled(Button)(({ children }) => ({
    ...(
        !children && {
            paddingLeft: 0,
            paddingRight: 0,
        }
    ),
}));

export function ApproveReject({
    action,
    requestConfirmation,
    onClick,
    forceLargeButton,
    noTextBreakpoint = "md",
    loading,
    confirmationMessage,
    isBulkAsync,
    disabled,
    report,
}: ApproveRejectProps) {
    const [showModal, setShowModal] = useState(false);

    const theme = useTheme();
    const hideButtonText = useMediaQuery(theme.breakpoints.down(noTextBreakpoint));

    /**
     * "main button" click handler
     */
    const handleClick = () => {
        report("act");
        if (requestConfirmation || isBulkAsync) {
            setShowModal(true);
            return;
        }
        handleAction();
    };

    const handleAction = () => {
        setShowModal(false);
        report("act_confirm");
        onClick();
    };

    return (
        <>
            <StyledButton
                color={action === "approve" ? "success" : "danger"}
                variant={"pill"}
                startIcon={action === "approve" ? <Check24Icon /> : <Clear24Icon />}
                onClick={handleClick}
                loading={loading}
                disabled={disabled}
                size={hideButtonText && !forceLargeButton ? "small" : "medium"}
            >
                {!hideButtonText && (action === "approve" ? translate("Approve") : translate("Reject"))}
            </StyledButton>
            <Modal open={showModal} onClose={() => setShowModal(false)}>
                <ModalTitle>
                    {action === "approve" ? translate("Approve confirmation") : translate("Reject confirmation")}
                </ModalTitle>
                <ModalContent>{HtmlReactParser(confirmationMessage)}</ModalContent>
                <ModalActions>
                    <Button variant={"borderless"} color={"secondary"} onClick={() => setShowModal(false)}>
                        {translate("Cancel")}
                    </Button>
                    <Button color={action === "approve" ? "success" : "danger"} onClick={handleAction}>
                        {action === "approve" ? translate("Approve") : translate("Reject")}
                    </Button>
                </ModalActions>
            </Modal>
        </>
    );
}
